"use client";

import { clsx } from "clsx";
import { Loader2, Mail, Phone, Send, User } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { InputField, TextareaField } from "./FormField";
type FormData = {
  name: string;
  email: string;
  phone: string;
  message: string;
  website: string;
};
type Status = {
  type: "idle" | "loading" | "success" | "error";
  message: string;
};
const INITIAL_FORM: FormData = {
  name: "",
  email: "",
  phone: "",
  message: "",
  website: ""
};
export function ContactForm() {
  const t = useTranslations("contact");
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [formData, setFormData] = React.useState<FormData>(INITIAL_FORM);
  const [status, setStatus] = React.useState<Status>({
    type: "idle",
    message: ""
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setStatus({
      type: "loading",
      message: ""
    });
    try {
      const captchaToken = await recaptchaRef.current?.executeAsync();
      recaptchaRef.current?.reset();
      if (!captchaToken) {
        setStatus({
          type: "error",
          message: t("form.messages.captchaError")
        });
        return;
      }
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          captchaToken
        })
      });
      const data = await response.json();
      if (response.ok) {
        setStatus({
          type: "success",
          message: t("form.messages.success")
        });
        setFormData(INITIAL_FORM);
      } else {
        setStatus({
          type: "error",
          message: data.error || t("form.messages.error")
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus({
        type: "error",
        message: t("form.messages.error")
      });
    }
  };
  const requiredLabel = t("form.fields.required");
  const isLoading = status.type === "loading";
  return <>
      <div className="max-w-3xl mx-auto mb-12 text-center">
        <h3 className="text-2xl md:text-3xl font-serif text-forest-green mb-4">
          {t("form.title")}
        </h3>
        <p className="text-wood-natural">{t("form.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white-cream rounded-2xl p-8 md:p-12 shadow-lg">
        {/* Honeypot field */}
        <input type="text" name="website" value={formData.website} onChange={handleChange} style={{
        position: "absolute",
        left: "-9999px"
      }} tabIndex={-1} autoComplete="off" aria-hidden="true" />

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <InputField id="name" name="name" type="text" label={t("form.fields.name")} value={formData.name} onChange={handleChange} placeholder={t("form.placeholders.name")} required requiredLabel={requiredLabel} icon={<User className="w-5 h-5" />} />
          <InputField id="email" name="email" type="email" label={t("form.fields.email")} value={formData.email} onChange={handleChange} placeholder={t("form.placeholders.email")} required requiredLabel={requiredLabel} icon={<Mail className="w-5 h-5" />} />
        </div>

        <div className="mb-6">
          <InputField id="phone" name="phone" type="tel" label={t("form.fields.phone")} value={formData.phone} onChange={handleChange} placeholder={t("form.placeholders.phone")} icon={<Phone className="w-5 h-5" />} />
        </div>

        <div className="mb-6">
          <TextareaField id="message" name="message" label={t("form.fields.message")} value={formData.message} onChange={handleChange} placeholder={t("form.placeholders.message")} required requiredLabel={requiredLabel} />
        </div>

        <div className="mb-6 flex justify-center">
          <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""} />
        </div>

        {status.message && <div className={clsx("mb-6 p-4 rounded-lg text-sm", status.type === "success" && "bg-green-100 text-green-800 border border-green-200", status.type === "error" && "bg-red-100 text-red-800 border border-red-200")}>
            {status.message}
          </div>}

        <button type="submit" disabled={isLoading} className={clsx("w-full flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-medium text-white-cream transition-all", isLoading ? "bg-wood-natural cursor-not-allowed" : "bg-terracotta hover:bg-terracotta-light hover:shadow-lg")}>
          {isLoading ? <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("form.sending")}
            </> : <>
              <Send className="w-5 h-5" />
              {t("form.submit")}
            </>}
        </button>

        <p className="mt-6 text-xs text-wood-natural text-center">
          {t("form.privacy")}{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">
            {t("form.privacyLink")}
          </a>{" "}
          {t("form.and")}{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">
            {t("form.termsLink")}
          </a>{" "}
          {t("form.privacyEnd")}
        </p>
      </form>
    </>;
}